// Appels d'API de l'ANNUAIRE DES UTILISATEURS (migration 052) : les vraies
// personnes du produit, vivier des études et des entretiens.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Seniority } from '../api/types';

export type Engagement = 'faible' | 'moyen' | 'fort' | 'ambassadeur';

export const ENGAGEMENTS: { value: Engagement; label: string }[] = [
  { value: 'faible', label: 'Faible' },
  { value: 'moyen', label: 'Moyen' },
  { value: 'fort', label: 'Fort' },
  { value: 'ambassadeur', label: 'Ambassadeur' },
];

export const engagementLabel = (v: Engagement | null | undefined) =>
  ENGAGEMENTS.find((e) => e.value === v)?.label ?? null;

export interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  role: string | null;
  seniority: Seniority | null;
  engagement: Engagement | null;
  pool: string | null;
  key_user: boolean;
  domain_expert: boolean;
  persona_id: string | null;
  persona_name: string | null;
  /** Elle a demandé à ne plus être sollicitée : visible, mais plus choisie. */
  do_not_contact: boolean;
  notes: string | null;
  source: 'manuel' | 'import';
  /** Nombre d'invitations reçues, et la dernière. */
  invitations: number;
  last_invited_at: string | null;
  /** Ses études, ses invitations des 90 derniers jours, et l'invitation EN COURS. */
  studies: number;
  recent_invitations: number;
  active_invite: { study_id: string; study: string | null; status: 'pending' | 'accepted'; starts_at: string | null } | null;
}

/** Trois invitations ou plus en 90 jours : on évite de la solliciter encore. */
export const OVER_SOLICITED = 3;

export interface TimelineEvent {
  at: string;
  kind: 'invite' | 'accept' | 'decline' | 'cancel' | 'reschedule' | 'done' | 'no_show' | 'verbatims';
  label: string;
  detail?: string | null;
  studyId: string;
  study: string | null;
  by: string | null;
}

export interface PersonDetail {
  person: Person;
  persona: { id: string; name: string; role: string | null } | null;
  stats: { invitations: number; responses: number; sessions: number; studies: number; verbatims: number; interactions: number };
  agenda: {
    invitationId: string; studyId: string; study: string | null; startsAt: string; durationMin: number;
    mode: 'remote' | 'onsite'; provider: 'google_meet' | 'teams' | null; location: string | null;
    meetingUrl: string | null; organizer: string | null;
  }[];
  timeline: TimelineEvent[];
  invitations: {
    id: string; studyId: string; study: string | null; subject: string; booking: string; startsAt: string | null;
    slots: number; organizer: string | null; createdAt: string; respondedAt: string | null; reason: string | null;
  }[];
  interviews: { id: string; studyId: string; study: string | null; conductedOn: string | null; recorded: boolean; transcribed: boolean; verbatims: number }[];
  studies: { id: string; label: string | null; invitations: number; interviews: number }[];
  canWrite: boolean;
}

export function usePersonDetail(personId: string) {
  return useQuery({
    queryKey: ['people', 'detail', personId] as const,
    queryFn: () => api.get<PersonDetail>(`/api/people/${encodeURIComponent(personId)}`),
    enabled: Boolean(personId),
    staleTime: 15 * 1000,
  });
}

// Une sélection faite dans l'annuaire, à reprendre sur la page « Inviter » :
// lue une fois, comme un onglet à ouvrir (`presetTab`).
let presetSelection: string[] = [];
export function presetPeople(ids: string[]) { presetSelection = [...ids]; }
export function takePresetPeople(): string[] {
  const ids = presetSelection;
  presetSelection = [];
  return ids;
}

export interface PeopleResponse {
  people: Person[];
  pools: string[];
  roles: string[];
  canWrite: boolean;
}

/** Ce que l'on envoie pour créer ou modifier une fiche. */
export interface PersonInput {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  role?: string | null;
  seniority?: Seniority | null;
  engagement?: Engagement | null;
  pool?: string | null;
  keyUser?: boolean;
  domainExpert?: boolean;
  personaId?: string | null;
  doNotContact?: boolean;
  notes?: string | null;
}

/** Les champs qu'une colonne importée peut remplir. */
export type ImportField =
  | 'firstName' | 'lastName' | 'fullName' | 'email' | 'phone' | 'jobTitle' | 'role'
  | 'seniority' | 'engagement' | 'pool' | 'keyUser' | 'domainExpert' | 'notes';

export const IMPORT_FIELDS: { value: ImportField; label: string }[] = [
  { value: 'firstName', label: 'Prénom' },
  { value: 'lastName', label: 'Nom' },
  { value: 'fullName', label: 'Nom complet' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'jobTitle', label: 'Fonction' },
  { value: 'role', label: 'Rôle' },
  { value: 'seniority', label: 'Séniorité' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'pool', label: 'Pool' },
  { value: 'keyUser', label: 'Key user' },
  { value: 'domainExpert', label: 'Expert métier' },
  { value: 'notes', label: 'Notes' },
];

export interface ImportedPerson {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  role: string | null;
  seniority: Seniority | null;
  engagement: Engagement | null;
  pool: string | null;
  keyUser: boolean;
  domainExpert: boolean;
}

export interface ImportReport {
  headers: string[];
  mapping: (ImportField | null)[];
  suggested: (ImportField | null)[];
  total: number;
  preview: ImportedPerson[];
  created: number;
  updated: number;
  skipped: { line: number; name?: string; reason: string }[];
  warnings: { line: number; name: string; warning: string }[];
  dryRun: boolean;
}

export interface ImportRequest {
  text: string;
  mapping?: (ImportField | null)[];
  updateExisting?: boolean;
  dryRun: boolean;
}

/** « Lina Martin », ou ce qu'on a. */
export const personName = (p: Pick<Person, 'first_name' | 'last_name'>) =>
  [p.first_name, p.last_name].filter(Boolean).join(' ');

const key = (projectId: string) => ['people', projectId] as const;
const base = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/people`;

export function usePeople(projectId: string) {
  return useQuery({
    queryKey: key(projectId),
    queryFn: () => api.get<PeopleResponse>(base(projectId)),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export function useCreatePerson(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PersonInput) => api.post<{ id: string }>(base(projectId), input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}

export function useUpdatePerson(_projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PersonInput }) =>
      api.request<{ id: string }>(`/api/people/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['people'] }),
  });
}

/** Efface la personne ET ses invitations (droit à l'effacement). */
export function useErasePerson(_projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ erased: boolean }>(`/api/people/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['session-invitations'] });
    },
  });
}

export function useImportPeople(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ImportRequest) => api.post<ImportReport>(`${base(projectId)}/import`, req),
    onSuccess: (report) => {
      if (!report.dryRun) void qc.invalidateQueries({ queryKey: key(projectId) });
    },
  });
}
